import type { FormBuilderData } from "@/features/forms/api/formBuilderApi";
import {
    type FormTranslationsT,
    type TranslationUpsertT,
    hasFormTranslations,
    useFormTranslations,
    useUpsertTranslation,
} from "@/features/forms/api/formTranslationsBuilderApi";
import { isFormTranslateEnabled, translateFormCopy } from "@/features/forms/ai/translateFormCopy";
import { DEFAULT_LOCALE, LOCALE_LABELS, SUPPORTED_LOCALES, type SupportedLocale } from "@patient-watch/i18n";
import { diffWords } from "@/features/forms/components/form-builder/translationDiff";
import { cn, ui } from "@patient-watch/ui";
import { toast } from "@patient-watch/ui/sonner";
import { Languages, Loader2, Sparkles } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { BuilderStepOff } from "./BuilderStepOff";

const TRANSLATABLE_LOCALES = SUPPORTED_LOCALES.filter((locale) => locale !== DEFAULT_LOCALE);

interface ScaleOption {
    label: string;
    value: unknown;
}

function scaleOptions(raw: unknown): ScaleOption[] {
    if (!Array.isArray(raw)) return [];
    return raw.flatMap((item) =>
        item && typeof item === "object" && "label" in item
            ? [{ label: String((item as ScaleOption).label ?? ""), value: (item as ScaleOption).value }]
            : []
    );
}

/** Looks up the translation to save for a field: a new value when given, otherwise what is saved now. */
type ValueOf = (fieldKey: string) => string;

/** One translatable field: the English source, and the saved translation ("" when there is none). */
interface FieldT {
    key: string;
    label: string;
    source: string;
    value: string;
    multiline?: boolean;
    /** The row this field is saved on. Fields on one row are saved together. */
    rowKey: string;
}

interface SectionT {
    title: string;
    fields: FieldT[];
}

interface LanguageModelT {
    sections: SectionT[];
    /** Builds the whole translation row for a rowKey, so several fields can be saved in one write. */
    rows: Map<string, (valueOf: ValueOf) => TranslationUpsertT>;
}

function buildModel(form: FormBuilderData, data: FormTranslationsT, locale: SupportedLocale): LanguageModelT {
    const rows = new Map<string, (valueOf: ValueOf) => TranslationUpsertT>();
    const sections: SectionT[] = [];
    const orNull = (value: string) => value.trim() || null;

    // Cover
    const formRow = data.forms.find((row) => row.locale === locale);
    const formColumns = [
        ["form_name", "Name", false],
        ["form_caption", "Caption", true],
        ["form_description", "Description", true],
        ["form_instructions", "Instructions", true],
    ] as const;
    const coverFields: FieldT[] = formColumns.flatMap(([column, label, multiline]) => {
        const source = (form[column] as string | null)?.trim();
        return source
            ? [{ key: `form-${column}`, label, source, value: formRow?.[column] ?? "", multiline, rowKey: "form" }]
            : [];
    });
    rows.set("form", (valueOf) => ({
        table: "form_translations",
        row: {
            form_id: form.form_id,
            locale,
            ...Object.fromEntries(formColumns.map(([column]) => [column, orNull(valueOf(`form-${column}`))])),
        },
    }));
    sections.push({ title: "Cover", fields: coverFields });

    for (const page of form.pages) {
        const fields: FieldT[] = [];
        const pageRow = data.pages.find((row) => row.page_id === page.page_id && row.locale === locale);
        const pageRowKey = `page-${page.page_id}`;
        const pageColumns = [
            ["page_label", "Page title", false],
            ["page_description", "Page text", true],
        ] as const;
        for (const [column, label, multiline] of pageColumns) {
            const source = (page[column] as string | null | undefined)?.trim();
            if (source) {
                fields.push({
                    key: `${pageRowKey}-${column}`,
                    label,
                    source,
                    value: pageRow?.[column] ?? "",
                    multiline,
                    rowKey: pageRowKey,
                });
            }
        }
        rows.set(pageRowKey, (valueOf) => ({
            table: "page_translations",
            row: {
                page_id: page.page_id,
                locale,
                ...Object.fromEntries(
                    pageColumns.map(([column]) => [column, orNull(valueOf(`${pageRowKey}-${column}`))])
                ),
            },
        }));

        for (const block of page.blocks) {
            const blockRow = data.blocks.find((row) => row.block_id === block.block_id && row.locale === locale);
            const blockRowKey = `block-${block.block_id}`;
            const blockColumns = [
                ["content", "Text block"],
                ["caption", "Caption"],
            ] as const;
            for (const [column, label] of blockColumns) {
                const source = block[column]?.trim();
                if (source) {
                    fields.push({
                        key: `${blockRowKey}-${column}`,
                        label,
                        source,
                        value: blockRow?.[column] ?? "",
                        multiline: true,
                        rowKey: blockRowKey,
                    });
                }
            }
            rows.set(blockRowKey, (valueOf) => ({
                table: "block_translations",
                row: {
                    block_id: block.block_id,
                    locale,
                    content: orNull(valueOf(`${blockRowKey}-content`)),
                    caption: orNull(valueOf(`${blockRowKey}-caption`)),
                },
            }));
        }

        for (const input of page.inputs) {
            const inputRow = data.inputs.find((row) => row.input_id === input.input_id && row.locale === locale);
            const rowKey = `input-${input.input_id}`;
            const options = (input.options ?? []).filter((option) => option.trim());
            const savedOptions = inputRow?.options ?? [];
            const scale = scaleOptions(input.scale_options);
            const savedScale = scaleOptions(inputRow?.scale_options);

            if (input.input_label?.trim()) {
                fields.push({
                    key: `${rowKey}-label`,
                    label: "Question",
                    source: input.input_label,
                    value: inputRow?.input_label ?? "",
                    rowKey,
                });
            }
            if (input.input_caption?.trim()) {
                fields.push({
                    key: `${rowKey}-caption`,
                    label: "Hint",
                    source: input.input_caption,
                    value: inputRow?.input_caption ?? "",
                    multiline: true,
                    rowKey,
                });
            }
            options.forEach((option, index) => {
                const saved = savedOptions[index];
                fields.push({
                    key: `${rowKey}-option-${index}`,
                    label: `Option ${index + 1}`,
                    source: option,
                    value: saved && saved !== option ? saved : "",
                    rowKey,
                });
            });
            scale.forEach((option, index) => {
                const saved = savedScale[index]?.label;
                fields.push({
                    key: `${rowKey}-scale-${index}`,
                    label: `Answer ${index + 1}`,
                    source: option.label,
                    value: saved && saved !== option.label ? saved : "",
                    rowKey,
                });
            });

            rows.set(rowKey, (valueOf) => {
                // A translated list replaces the whole list, so gaps fall back to the English text.
                const nextOptions = options.map((source, index) => valueOf(`${rowKey}-option-${index}`) || source);
                // Scale values stay as the source so scoring is unchanged; only labels are translated.
                const nextScale = scale.map((source, index) => ({
                    label: valueOf(`${rowKey}-scale-${index}`) || source.label,
                    value: source.value,
                }));
                return {
                    table: "input_translations",
                    row: {
                        input_id: input.input_id,
                        locale,
                        input_label: orNull(valueOf(`${rowKey}-label`)),
                        input_caption: orNull(valueOf(`${rowKey}-caption`)),
                        options: nextOptions.some((item, i) => item !== options[i]) ? nextOptions : null,
                        scale_options: nextScale.some((item, i) => item.label !== scale[i]?.label)
                            ? (nextScale as never)
                            : null,
                    },
                };
            });
        }

        if (fields.length > 0) {
            sections.push({ title: page.page_label?.trim() || `Page ${page.page_index}`, fields });
        }
    }

    return { sections, rows };
}

/** Form builder Language step: translate the cover, block titles, questions and answers for each locale. */
export function FormLanguageSettings({
    form,
    formId,
    canEdit,
    locale,
    onLocaleChange,
}: {
    form: FormBuilderData;
    formId: string;
    canEdit: boolean;
    locale: SupportedLocale;
    onLocaleChange: (locale: SupportedLocale) => void;
}) {
    const { data, isLoading } = useFormTranslations(form);
    const upsert = useUpsertTranslation(formId);
    const [isDrafting, setIsDrafting] = useState(false);
    const [onlyMissing, setOnlyMissing] = useState(false);
    const [settingUp, setSettingUp] = useState(false);
    const isOff = !isLoading && !hasFormTranslations(data) && !settingUp;

    const modelByLocale = useMemo(
        () =>
            data
                ? (Object.fromEntries(
                      TRANSLATABLE_LOCALES.map((candidate) => [candidate, buildModel(form, data, candidate)])
                  ) as Record<SupportedLocale, LanguageModelT>)
                : null,
        [form, data]
    );

    const model = modelByLocale?.[locale];
    const fields = useMemo(() => model?.sections.flatMap((section) => section.fields) ?? [], [model]);
    const savedValue = (key: string) => fields.find((field) => field.key === key)?.value ?? "";

    /** Saves new values for some fields, one write per row. */
    const saveValues = async (overrides: Map<string, string>) => {
        if (!model) return;
        const valueOf: ValueOf = (key) => (overrides.has(key) ? (overrides.get(key) ?? "") : savedValue(key));
        const rowKeys = new Set(fields.filter((field) => overrides.has(field.key)).map((field) => field.rowKey));
        for (const rowKey of rowKeys) {
            const build = model.rows.get(rowKey);
            if (build) await upsert.mutateAsync(build(valueOf));
        }
    };

    const draftWithAi = async () => {
        const missing = fields.filter((field) => !field.value.trim());
        if (missing.length === 0) {
            toast.info(`Everything is already translated into ${LOCALE_LABELS[locale]}.`);
            return;
        }
        setIsDrafting(true);
        try {
            const drafts = await translateFormCopy(
                missing.map((field) => ({ key: field.key, label: field.label, source: field.source })),
                locale
            );
            const overrides = new Map(Object.entries(drafts).filter(([, value]) => value.trim()));
            await saveValues(overrides);
            toast.success(
                `Drafted ${overrides.size} ${LOCALE_LABELS[locale]} translations. Check each one before patients see it.`
            );
        } catch (error) {
            toast.error(error instanceof Error ? error.message : "AI translation failed.");
        } finally {
            setIsDrafting(false);
        }
    };

    const missingCount = fields.filter((field) => !field.value.trim()).length;

    return (
        <section className="bg-background border rounded-xl overflow-hidden" data-testid="form-language-settings">
            <div className="flex justify-between items-start gap-4 px-5 py-4 border-b">
                <div className="min-w-0">
                    <h2 className="font-semibold text-sm">Language</h2>
                    <p className="text-muted-foreground text-xs text-pretty">
                        Patients see the form in their language when a translation exists, and in English where one is
                        missing. Validated questionnaires need the licensed translation word for word.
                    </p>
                </div>
                {canEdit && !isOff && isFormTranslateEnabled() ? (
                    <ui.Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="gap-1.5 shrink-0"
                        disabled={isDrafting || isLoading || missingCount === 0}
                        onClick={() => void draftWithAi()}
                        title="Drafts only the fields that are empty. Saved drafts are not licensed translations."
                        data-testid="form-language-ai"
                    >
                        {isDrafting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                        {isDrafting ? "Drafting…" : `Draft ${missingCount} with AI`}
                    </ui.Button>
                ) : null}
            </div>

            {isOff ? (
                <BuilderStepOff
                    icon={Languages}
                    title="No translations"
                    description={
                        canEdit
                            ? "Patients see this form in English."
                            : "Patients see this form in English. Only admins can add translations."
                    }
                    testId="form-language-off"
                >
                    {canEdit ? (
                        <ui.Button type="button" size="sm" onClick={() => setSettingUp(true)}>
                            Add a translation
                        </ui.Button>
                    ) : null}
                </BuilderStepOff>
            ) : (
                <>
                    <div className="flex flex-wrap gap-2 px-5 py-3 border-b" role="tablist" aria-label="Languages">
                        {TRANSLATABLE_LOCALES.map((candidate) => {
                            const candidateFields = modelByLocale?.[candidate]?.sections.flatMap((s) => s.fields) ?? [];
                            const done = candidateFields.filter((field) => field.value.trim()).length;
                            const selected = candidate === locale;
                            return (
                                <button
                                    key={candidate}
                                    type="button"
                                    role="tab"
                                    aria-selected={selected}
                                    onClick={() => onLocaleChange(candidate)}
                                    className={cn(
                                        "inline-flex items-center gap-1.5 px-2.5 py-1 border rounded-full text-xs transition-colors",
                                        selected
                                            ? "border-teal-500 bg-teal-50 text-foreground dark:bg-teal-950/40"
                                            : "text-muted-foreground hover:text-foreground",
                                        done === 0 && !selected && "opacity-60"
                                    )}
                                >
                                    {LOCALE_LABELS[candidate]}
                                    <span className="tabular-nums text-2xs text-muted-foreground">
                                        {done}/{candidateFields.length}
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    {isLoading ? (
                        <div className="space-y-2 p-6">
                            <ui.Skeleton className="w-1/2 h-4" />
                            <ui.Skeleton className="w-full h-8" />
                        </div>
                    ) : (
                        <div className="space-y-6 p-6">
                            <label className="flex items-center gap-2 text-xs text-muted-foreground">
                                <input
                                    type="checkbox"
                                    checked={onlyMissing}
                                    onChange={(event) => setOnlyMissing(event.target.checked)}
                                    data-testid="form-language-only-missing"
                                />
                                Show only fields that are not translated yet ({missingCount})
                            </label>
                            {(model?.sections ?? []).map((section) => {
                                const visible = onlyMissing
                                    ? section.fields.filter((field) => !field.value.trim())
                                    : section.fields;
                                if (visible.length === 0) return null;
                                const done = section.fields.filter((field) => field.value.trim()).length;
                                return (
                                    <div key={section.title} className="space-y-2">
                                        <p className="flex justify-between items-baseline font-medium text-sm">
                                            {section.title}
                                            <span className="tabular-nums font-normal text-2xs text-muted-foreground">
                                                {done}/{section.fields.length} translated
                                            </span>
                                        </p>
                                        <div className="border rounded-lg divide-y">
                                            {visible.map((field) => (
                                                <TranslationRow
                                                    key={`${locale}-${field.key}`}
                                                    field={field}
                                                    disabled={!canEdit || isDrafting}
                                                    onSave={(next) => void saveValues(new Map([[field.key, next]]))}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                            {!canEdit ? (
                                <p className="text-muted-foreground text-xs">Only admins can edit translations.</p>
                            ) : null}
                        </div>
                    )}
                </>
            )}
        </section>
    );
}

function TranslationRow({
    field,
    disabled,
    onSave,
}: {
    field: FieldT;
    disabled: boolean;
    onSave: (next: string) => void;
}) {
    const [draft, setDraft] = useState(field.value);
    useEffect(() => setDraft(field.value), [field.value]);

    const commit = () => {
        if (draft.trim() === field.value.trim()) return;
        onSave(draft.trim());
    };
    const Control = field.multiline ? ui.Textarea : ui.Input;

    const translated = field.value.trim().length > 0;
    const changes = translated ? diffWords(field.source, field.value) : [];
    const hasChanges = changes.some((part) => part.kind !== "same");

    return (
        <div
            className={cn(
                "gap-3 grid sm:grid-cols-2 px-3 py-2.5",
                !translated && "bg-amber-50/40 dark:bg-amber-950/10"
            )}
            data-translated={translated}
        >
            <div className="min-w-0">
                <p className="flex items-center gap-1.5 text-2xs text-muted-foreground">
                    <span
                        className={cn("rounded-full size-1.5", translated ? "bg-teal-500" : "bg-amber-500")}
                        aria-hidden
                    />
                    {field.label}
                    <span className="sr-only">{translated ? "translated" : "not translated"}</span>
                </p>
                <p className="text-sm text-pretty break-words">{field.source}</p>
            </div>
            <div className="space-y-1.5 min-w-0">
                <Control
                    value={draft}
                    disabled={disabled}
                    rows={field.multiline ? 2 : undefined}
                    aria-label={`${field.label} translation`}
                    placeholder="Not translated - English is shown"
                    onChange={(event: React.ChangeEvent<HTMLInputElement & HTMLTextAreaElement>) =>
                        setDraft(event.target.value)
                    }
                    onBlur={commit}
                    className="text-sm"
                />
                {translated ? (
                    <p className="text-xs text-pretty break-words text-muted-foreground" data-testid="translation-diff">
                        {hasChanges ? "Changed from English: " : "Same as English"}
                        {hasChanges
                            ? changes.map((part, index) =>
                                  part.kind === "same" ? (
                                      <span key={index}>{part.text}</span>
                                  ) : (
                                      <mark
                                          key={index}
                                          className={cn(
                                              "rounded-sm px-0.5",
                                              part.kind === "removed"
                                                  ? "bg-red-100 text-red-800 line-through dark:bg-red-950/50 dark:text-red-200"
                                                  : "bg-teal-100 text-teal-900 dark:bg-teal-950/50 dark:text-teal-100"
                                          )}
                                      >
                                          {part.text}
                                      </mark>
                                  )
                              )
                            : null}
                    </p>
                ) : null}
            </div>
        </div>
    );
}
